import { Typography, Link, Divider, Skeleton, Box, Stack, Chip, useTheme } from '@mui/material';
import { Facility, Occasion, PlayerData } from '@oskarjiang/matchi-types';
import { useFocusedOccasion } from '../../contexts/FocusedOccasionContext';
import { useQuery } from '@tanstack/react-query';
import { baseUrl } from '../../constants/endpoints';
import { useFocusedActivity } from '../../contexts/FocusedActivityContext';
import { useMemo } from 'react';
// Icons
import SportsIcon from '@mui/icons-material/Sports';
import PlaceIcon from '@mui/icons-material/Place';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

export const Header = () => {
	const theme = useTheme();
	const { focusedOccasion } = useFocusedOccasion();
	const { focusedActivity } = useFocusedActivity();
        const { data: facilities, isLoading } = useQuery<Facility[]>({
                queryKey: ['facilities'],
                queryFn: () => fetch(`${baseUrl}/facilities`).then((res) => res.json()),
                staleTime: 7 * 24 * 60 * 60 * 1000, // 1 week - rarely changes
        });

        const { data: occasionParticipants, isLoading: isLoadingParticipants } = useQuery<Occasion>({
                queryKey: ['participants', focusedOccasion?.id],
                queryFn: () => fetch(`${baseUrl}/occasion/${focusedOccasion?.id}`).then((res) => res.json()),
                enabled: !!focusedOccasion?.id,
        });

        const { data: playerData, isLoading: isLoadingPlayerData } = useQuery<PlayerData[]>({
                queryKey: ['player-data'],
                queryFn: () => fetch(`${baseUrl}/playerData`).then((res) => res.json()),
                staleTime: 7 * 24 * 60 * 60 * 1000, // 1 week - rarely changes
        });

        const averageLevel = useMemo(() => {
                if (!occasionParticipants?.occasionParticipants || !playerData) {
                        return undefined;
                }

                const participantLevels = occasionParticipants.occasionParticipants
                        .map((participant) => playerData.find((player) => player.player.userId === participant.userId)?.averageLevel)
                        .filter((level): level is number => level !== undefined && level !== null);

                if (!participantLevels.length) {
                        return undefined;
                }

                const total = participantLevels.reduce((sum, level) => sum + level, 0);
                return total / participantLevels.length;
        }, [occasionParticipants?.occasionParticipants, playerData]);

        const renderAverageLevel = () => {
                if (isLoadingParticipants || isLoadingPlayerData) {
                        return <Skeleton variant="rounded" width={140} height={32} />;
                }

                return (
                        <Chip
                                label={`Avg level: ${averageLevel !== undefined ? averageLevel.toPrecision(2) : '-'}`}
                                color="primary"
                                variant="outlined"
                                sx={{ fontWeight: 600 }}
                        />
                );
        };

	const facility = facilities?.find((facility) => facility.id === focusedActivity?.facilityId);

	// Format time in HH:MM format
	const formatTime = (timeString?: string) => {
		if (!timeString) return '';
		const date = new Date(timeString);
		return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
	};

	// Format date
	const formatDate = (timeString?: string) => {
		if (!timeString) return '';
		const date = new Date(timeString);
		return date.toLocaleDateString([], { weekday: 'long', month: 'short', day: 'numeric' });
	};
	return (
                <Box sx={{ pt: 1, pb: 3 }}>
                        <Box
                                sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        gap: 1,
                                        mb: 1,
                                        flexWrap: 'wrap',
                                }}
                        >
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', flex: 1, minWidth: 0 }}>
                                        <SportsIcon sx={{ mr: 1, color: theme.palette.primary.main, transform: 'rotate(45deg)' }} />
                                        <Typography
                                                variant="h5"
                                                fontWeight="bold"
                                                sx={{
                                                        fontSize: { xs: '1.2rem', sm: '1.4rem' },
                                                        wordBreak: 'break-word',
                                                        width: { xs: 'calc(100% - 32px)', sm: 'auto' },
                                                }}
                                        >
                                                {focusedActivity?.name || <Skeleton width="100%" />}
                                        </Typography>
                                </Box>
                                {renderAverageLevel()}
                        </Box>
			<Stack spacing={2} sx={{ mt: 3 }}>
				{/* Facility */}
				<Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
					<PlaceIcon sx={{ mr: 1, color: theme.palette.primary.main, flexShrink: 0 }} />
					{isLoading ? (
						<Skeleton width="80%" />
					) : (
						<Link
							href={facility?.webUrl}
							variant="subtitle1"
							color="primary"
							target="_blank"
							sx={{
								display: 'flex',
								alignItems: 'center',
								textDecoration: 'none',
								'&:hover': { textDecoration: 'underline' },
								wordBreak: 'break-word',
								lineHeight: 1.4,
							}}
						>
							{facility?.name}
							<OpenInNewIcon fontSize="small" sx={{ ml: 0.5, flexShrink: 0 }} />
						</Link>
					)}
				</Box>

				{/* Date & Time */}
				<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
					{' '}
					<Box sx={{ display: 'flex', alignItems: 'center' }}>
						<CalendarTodayIcon sx={{ mr: 1, color: theme.palette.primary.main, flexShrink: 0 }} />
						<Typography variant="body1" sx={{ wordBreak: 'break-word' }}>
							{focusedOccasion?.startTime ? formatDate(focusedOccasion.startTime) : <Skeleton width="80%" />}
						</Typography>
					</Box>
					<Box sx={{ display: 'flex', alignItems: 'center' }}>
						<AccessTimeIcon sx={{ mr: 1, color: theme.palette.primary.main, flexShrink: 0 }} />
						<Typography variant="body1">
							{focusedOccasion?.startTime && focusedOccasion?.endTime ? (
								`${formatTime(focusedOccasion.startTime)} - ${formatTime(focusedOccasion.endTime)}`
							) : (
								<Skeleton width="60%" />
							)}
						</Typography>
					</Box>
				</Box>

				{/* Participants & Price */}
				<Stack
					direction={{ xs: 'column', sm: 'row' }}
					spacing={{ xs: 1, sm: 2 }}
					alignItems={{ xs: 'flex-start', sm: 'center' }}
					sx={{ mt: 1 }}
				>
					<Chip
						icon={<PeopleAltIcon />}
						label={`${focusedOccasion?.participants || 0}/${focusedOccasion?.maxNumParticipants || 0} participants`}
						color={focusedOccasion?.participants === focusedOccasion?.maxNumParticipants ? 'error' : 'success'}
						variant="outlined"
						sx={{ maxWidth: '100%', height: 'auto', '& .MuiChip-label': { whiteSpace: 'normal', lineHeight: 1.5, py: 0.5 } }}
					/>

					{focusedOccasion?.basePrice && (
						<Chip
							icon={<LocalOfferIcon />}
							label={`${focusedOccasion.basePrice} SEK`}
							color="primary"
							variant="outlined"
							sx={{ maxWidth: '100%' }}
						/>
					)}
				</Stack>
			</Stack>{' '}
			{/* Description */}
			{focusedActivity?.teaser && (
				<>
					<Divider sx={{ my: 2 }} />
					<Typography
						variant="body2"
						sx={{
							fontStyle: 'italic',
							color: theme.palette.text.secondary,
							wordBreak: 'break-word',
							overflowWrap: 'break-word',
						}}
					>
						{focusedActivity.teaser}
					</Typography>
				</>
			)}
		</Box>
	);
};
